import {KeyboardSensor} from '@dnd-kit/core';
import type {
  Announcements,
  KeyboardSensorOptions,
  SensorInstance,
  SensorProps
} from '@dnd-kit/core';

export interface TabThroughKeyboardSensorOptions extends KeyboardSensorOptions {
  /** Called when Tab ends the pickup, just before the drag cancels. */
  onTab?: () => void;
}

/**
 * dnd-kit's keyboard sensor with Tab let through. Stock, Tab drops the item and keeps focus where
 * it is. Here Tab cancels the drag without cancelling the key, so focus moves on to the next
 * control and the item snaps back to where it was.
 */
export class TabThroughKeyboardSensor implements SensorInstance {
  static activators = KeyboardSensor.activators;

  autoScrollEnabled = false;

  constructor(props: SensorProps<TabThroughKeyboardSensorOptions>) {
    const ownerDocument = (props.event.target as Node | null)?.ownerDocument ?? document;
    const stopListening = () => ownerDocument.removeEventListener('keydown', handleKeyDown);

    const keyboard = new KeyboardSensor({
      ...props,
      onEnd: () => {
        stopListening();
        props.onEnd();
      },
      onCancel: () => {
        stopListening();
        props.onCancel();
      }
    });

    function handleKeyDown(event: KeyboardEvent) {
      if (event.code !== 'Tab' && event.key !== 'Tab') return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      // `detach` removes the sensor's own key and window listeners. dnd-kit does not export it.
      keyboard['detach']();
      stopListening();
      props.options.onTab?.();
      props.onCancel();
    }

    ownerDocument.addEventListener('keydown', handleKeyDown);
  }
}

/** Whether Tab ended the latest drag, shared by a drag context's handlers and its announcements. */
export interface TabAway {
  tabbedAway: boolean;
}

export const createTabAway = (): TabAway => ({tabbedAway: false});

export const setTabbedAway = (tabAway: TabAway, tabbedAway: boolean): void => {
  tabAway.tabbedAway = tabbedAway;
};

/**
 * A Tab is not announced as a cancel. It clears the pickup message instead: dnd-kit ignores a
 * missing message, so the old one would stay and picking the item up again would repeat it unheard.
 */
export const withoutTabCancel = (
  announcements: Announcements,
  tabAway: TabAway
): Announcements => ({
  ...announcements,
  onDragCancel: (args) => (tabAway.tabbedAway ? '' : announcements.onDragCancel?.(args))
});
